import { useState, useEffect, useRef } from 'react';
import type { AuthUserDTO } from '@scan2eat/shared';
import { RESTAURANT_ROLES } from '@scan2eat/shared';
import { apiFetch, ApiError } from '../api/client.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { DropdownItem, DropdownMenu } from '../components/DropdownMenu.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { Modal } from '../components/Modal.js';
import { initials } from '../lib/initials.js';
import { labels } from '../labels.js';

export function TeamPage() {
  const [users, setUsers] = useState<AuthUserDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState<AuthUserDTO | null>(null);
  const [adding, setAdding] = useState(false);
  const [deactivating, setDeactivating] = useState<AuthUserDTO | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const load = () => apiFetch<{ users: AuthUserDTO[] }>('/admin/users')
    .then((d) => { setUsers(d.users); setLoadError(false); })
    .catch(() => setLoadError(true))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const notify = (message: string) => {
    setBanner(message);
    window.setTimeout(() => setBanner(null), 5000);
  };

  const deactivate = async (u: AuthUserDTO) => {
    setDeactivating(null);
    try {
      await apiFetch(`/admin/users/${u.id}`, { method: 'DELETE' });
      await load();
    } catch {
      notify(labels.errors.generic);
    }
  };

  const activeCount = users.filter((u) => u.isActive).length;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{labels.team.title}</h1>
          <p className="page-subtitle">{loading ? labels.team.subtitle : labels.team.count(activeCount)}</p>
        </div>
        <div className="page-actions">
          <button onClick={() => setAdding(true)} className="btn-primary">
            <Icon name="plus" size={18} />
            {labels.team.addAccount}
          </button>
        </div>
      </header>

      {banner && (
        <p role="alert" className="notice notice--warning">
          <Icon name="alert" size={18} />
          {banner}
        </p>
      )}

      {loadError ? (
        <ErrorState onRetry={load} />
      ) : !loading && users.length === 0 ? (
        <div className="card"><EmptyState icon="users" text={labels.team.empty} /></div>
      ) : (
        <div className="card card--flush">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{labels.team.member}</th>
                  <th>{labels.team.roleLabel}</th>
                  <th>{labels.team.statusLabel}</th>
                  <th>{labels.team.lastLogin}</th>
                  <th><span className="visually-hidden">{labels.team.actions}</span></th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}><td colSpan={5}><div className="skeleton" style={{ height: '2.25rem' }} /></td></tr>
                    ))
                  : users.map((u) => (
                      <tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.6 }}>
                        <td>
                          <div className="row" style={{ gap: 'var(--space-3)' }}>
                            <span className="avatar avatar--soft">
                              {u.photoUrl ? <img src={u.photoUrl} alt="" /> : initials(u.firstName, u.lastName)}
                            </span>
                            <span style={{ display: 'grid', minWidth: 0 }}>
                              <strong className="truncate">{u.firstName} {u.lastName}</strong>
                              <span className="text-xs text-muted truncate">{u.email}</span>
                            </span>
                          </div>
                        </td>
                        <td><span className="badge badge--outline">{labels.team.roleLabels[u.role] ?? u.role}</span></td>
                        <td>
                          <span className={`badge badge--dot ${u.isActive ? 'badge--ready' : 'badge--neutral'}`}>
                            {u.isActive ? labels.team.activeState : labels.team.inactiveState}
                          </span>
                        </td>
                        <td className="text-muted text-sm">
                          {u.lastLoginAt
                            ? new Date(u.lastLoginAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                            : labels.profile.neverConnected}
                        </td>
                        <td className="num">
                          {u.isActive && (
                            <RowMenu
                              onEdit={() => setEditing(u)}
                              onDeactivate={() => setDeactivating(u)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(editing || adding) && (
        <UserForm
          user={editing ?? undefined}
          onSave={async (data) => {
            if (editing) {
              await apiFetch(`/admin/users/${editing.id}`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
              });
            } else {
              await apiFetch('/admin/users', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
              });
            }
            setEditing(null);
            setAdding(false);
            await load();
          }}
          onClose={() => { setEditing(null); setAdding(false); }}
        />
      )}

      {deactivating && (
        <ConfirmDialog
          title={labels.team.deactivateConfirm(`${deactivating.firstName} ${deactivating.lastName}`)}
          message={labels.team.deactivateHelp}
          confirmLabel={labels.team.deactivate}
          destructive
          onConfirm={() => void deactivate(deactivating)}
          onCancel={() => setDeactivating(null)}
        />
      )}
    </div>
  );
}

function RowMenu({ onEdit, onDeactivate }: { onEdit: () => void; onDeactivate: () => void }) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={anchor}
        type="button"
        className="btn-icon btn-icon--quiet btn-icon--sm"
        aria-label={labels.common.more}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" size={18} />
      </button>
      <DropdownMenu open={open} onClose={() => setOpen(false)} anchorRef={anchor}>
        <DropdownItem onClick={() => { setOpen(false); onEdit(); }} icon={<Icon name="edit" size={18} />}>
          {labels.team.edit}
        </DropdownItem>
        <DropdownItem onClick={() => { setOpen(false); onDeactivate(); }} icon={<Icon name="lock" size={18} />} danger>
          {labels.team.deactivate}
        </DropdownItem>
      </DropdownMenu>
    </div>
  );
}

function UserForm({ user, onSave, onClose }: { user?: AuthUserDTO; onSave: (d: Record<string, unknown>) => Promise<void>; onClose: () => void }) {
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>(user?.role ?? 'STAFF');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const data: Record<string, unknown> = { firstName, lastName, role };
      if (password) data.password = password;
      if (!user) data.email = email;
      await onSave(data);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'EMAIL_TAKEN') {
        setError(labels.team.emailTaken);
      } else {
        setError(labels.errors.generic);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={user ? labels.team.editAccount : labels.team.addAccount}
      description={user ? user.email : labels.team.addHelp}
      onClose={onClose}
      onSubmit={submit}
      locked={saving}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving}>{labels.team.cancel}</button>
          <button type="submit" className="btn-primary" disabled={saving}>{labels.team.save}</button>
        </>
      }
    >
      <div className="form-grid">
        {error && <p role="alert" className="notice notice--danger" style={{ margin: 0 }}><Icon name="alert" size={18} />{error}</p>}
        <div className="form-grid form-grid--2">
          <div className="field">
            <label htmlFor="user-first" className="field-label">{labels.team.firstName}</label>
            <input id="user-first" className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
          </div>
          <div className="field">
            <label htmlFor="user-last" className="field-label">{labels.team.lastName}</label>
            <input id="user-last" className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </div>
        </div>
        {!user && (
          <div className="field">
            <label htmlFor="user-email" className="field-label">{labels.team.email}</label>
            <input id="user-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
        )}
        <div className="field">
          <label htmlFor="user-password" className="field-label">{labels.team.password}</label>
          <input id="user-password" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required={!user} minLength={10} autoComplete="new-password" />
          <p className="field-hint">{user ? labels.team.passwordHint : labels.profile.passwordHint}</p>
        </div>
        <div className="field">
          <label htmlFor="user-role" className="field-label">{labels.team.roleLabel}</label>
          <select id="user-role" className="input" value={role} onChange={(e) => setRole(e.target.value)}>
            {RESTAURANT_ROLES.map((r) => <option key={r} value={r}>{labels.team.roleLabels[r] ?? r}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}
